<template>
  <MainLayout>
    <ContentPageHeader functionName="買取査定管理" pageName="買取明細">
    </ContentPageHeader>
    
    <div class="assessment-detail">
      <!-- 基本情報セクション -->
      <div class="basic-info-section">
        <div class="info-rows">
          <FormInfoRow icon="fa fa-hashtag" label="ID" :value="purchaseDetail.id" />
          <FormInfoRow icon="fa fa-calendar" label="買取日" :value="formatDate(purchaseDetail.purchaseDate)" />
          <FormInfoRow icon="fa fa-yen-sign" label="買取金額" :value="formatPrice(purchaseDetail.amount)" />
          <FormInfoRow icon="fa fa-sticky-note" label="備考" :value="purchaseDetail.remarks" />
        </div>
      </div>
      
      <!-- 商品テーブル -->
      <div class="items-table-section">
        <FormDataTable
          :items="purchaseDetail.items"
          :columns="columns"
          :perPage="50"
          :currentPage="1"
          :emptyMessage="'商品データがありません'"
          @page-change="onPageChange"
        >
        </FormDataTable>
      </div>
    </div>
  </MainLayout>
</template>

<script>
import MainLayout from '@/components/layout/MainLayout.vue'
import ContentPageHeader from '@/components/layout/ContentPageHeader.vue'
import FormInfoRow from '@/components/form/FormInfoRow.vue'
import FormDataTable from '@/components/form/FormDataTable.vue'
import apiService from '@/services/service.js'
import { showLoading, hideLoading } from '@/utils/loadingEventBus';

export default {
  name: 'PurchaseDetailView',
  components: {
    MainLayout,
    ContentPageHeader,
    FormInfoRow,
    FormDataTable
  },
  data() {
    return {
      isLoading: true,
      manufacturerOptions: [],
      ranks: [],
      originalCategories: [],
      purchaseDetail: {
        id: '',
        purchaseDate: '',
        amount: 0,
        remarks: '',
        items: []
      },
      // テーブルカラム定義
      columns: [
        { key: 'category', label: 'カテゴリ名', width: '15%', formatter: (value) => this.getCategoryLabel(value) },
        { key: 'maker', label: 'メーカー名', width: '15%', formatter: (value) => this.getManufacturerLabel(value) },
        { key: 'productName', label: '商品名', width: '35%' },
        { key: 'rank', label: 'ランク', width: '10%', formatter: (value) => this.getRankLabel(value) },
        { key: 'quantity', label: '個数', width: '10%', formatter: this.formatQuantity },
        { key: 'price', label: '買取金額', width: '10%', formatter: this.formatPrice },
        { key: 'isSatisfied', label: '不成立', width: '5%', formatter: (value) => (value === 0 ? '不成立' : '') }
      ]
    }
  },
  methods: {
    // 日付フォーマット
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(date);
    },
    // 金額フォーマット
    formatPrice(price) {
      return `¥${Number(price).toLocaleString()}`;
    },
    // 数量フォーマット
    formatQuantity(quantity) {
      return `${quantity}`;
    },
    // ページ変更ハンドラ
    onPageChange(page) {
      this.currentPage = page;
    },

    /**
     * メーカー一覧を取得する
     */
     async loadMakers() {
      try {
        const response = await apiService.getMakerList();
        if (!response || response.status !== 200) throw new Error('error status: ' + response.status);

        // APIレスポンスからメーカーオプションを設定
        this.manufacturerOptions = response.data.makers.map(maker => ({
          value: maker.id,
          label: maker.maker
        }));

      } catch (error) {
        console.error('メーカー一覧の取得に失敗しました:', error);
        throw error;
      }
    },
    
    /**
     * ランク一覧を取得する
     */
    async loadRanks() {
      try {
        const response = await apiService.getRankList();
        if (!response || response.status !== 200) throw new Error('error status: ' + response.status);

        // APIレスポンスからランクオプションを設定
        this.ranks = response.data.ranks.map(rank => ({
          value: rank.id,
          label: rank.rank
        }));

      } catch (error) {
        console.error('ランク一覧の取得に失敗しました:', error);
        throw error;
      }
    },
    /**
     * カテゴリ一覧を取得する
     */
    async loadCategories() {
      try {
        const response = await apiService.getCategoryList();
        if (!response || response.status !== 200) throw new Error('error status: ' + response.status);

        // APIレスポンスからカテゴリ一覧を設定
        this.originalCategories = JSON.parse(JSON.stringify(response.data.categories));
      } catch (error) {
        console.error('カテゴリ一覧の取得に失敗しました:', error);
        throw error;
      }
    },

    // 詳細データ取得
    async loadPurchaseDetail(purchaseSlipId) {
      this.isLoading = true;
      try {
        // 買取伝票情報を取得
        const response = await apiService.getPurchaseById(purchaseSlipId);
        if (!response || response.status !== 200) throw new Error('error status: ' + response.status);
         
        // 買取伝票情報がない
        if (!response.data || !response.data.commonPurchaseSlip) return;

        // 買取伝票情報を設定
        const commonPurchaseSlip = response.data.commonPurchaseSlip;
        const purchaseSlip = response.data.purchaseSlips;
        this.purchaseDetail = {
          id: commonPurchaseSlip.id ? commonPurchaseSlip.id : '',
          purchaseDate: commonPurchaseSlip.purchaseDate ? commonPurchaseSlip.purchaseDate : '',
          amount: purchaseSlip.reduce((sum, slip) => sum + (slip.isSatisfied === 1 ? (slip.purchaseAmount || 0) : 0), 0),
          remarks: commonPurchaseSlip.note ? commonPurchaseSlip.note : '',
          items: purchaseSlip?.map(slip => ({
            id: slip.id,
            category: slip.categoryId ? slip.categoryId : '',
            maker: slip.makerId ? slip.makerId : '',
            productName: slip.productName || '',
            rank: slip.rankId ? slip.rankId : '',
            quantity: slip.count || 1,
            price: slip.purchaseAmount || 0,
            isSatisfied: Number(slip.isSatisfied || 0)
          }))
        };
      } catch (error) {
        console.error('詳細データの取得に失敗しました:', error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
  
    /**
     * メーカー名を取得する
     */
    getManufacturerLabel(value) {
      const option = this.manufacturerOptions.find(opt => String(opt.value) === String(value));
      return option ? option.label : '';
    },

    /**
     * ランク名を取得する
     */
    getRankLabel(value) {
      // rankoptionsはカテゴリによって更新されるのでranksを利用する
      const option = this.ranks.find(opt => String(opt.value) === String(value));
      return option ? option.label : '';
    },

    /**
     * カテゴリ名を取得する
     */
    getCategoryLabel(value) {
      return this.originalCategories
        .flatMap(parent => parent.childCategories)
        .find(child => child.id === value)?.category || '';
    },

    async fetchData() {
      showLoading();
      try {
        await Promise.all([
          this.loadMakers(),
          this.loadRanks(),
          this.loadCategories()
        ]);

        if (this.$route.params.id) {
          this.loadPurchaseDetail(this.$route.params.id);
          return;
        }

        // 新規登録時の初期値設定
        this.purchase = this.defaultPurchase;
      } catch (error) {
        console.error('データの取得に失敗しました:', error);
        alert('詳細データの取得中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    }
  },
  async mounted() {
    const navigationFlag = sessionStorage.getItem('legitimateNavigation');
    if (navigationFlag !== 'PurchaseDetailView') {
      this.$router.push({ name: 'PurchaseHistory' });
      return;
    }
    sessionStorage.removeItem('legitimateNavigation');
    // 初期データの読み込み
    await this.fetchData();
  }
}
</script>

<style scoped>
.assessment-detail {
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 10px;
}

.basic-info-section {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.info-rows {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.items-table-section {
  background-color: white;
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
}

@media (max-width: 768px) {
  .info-rows {
    grid-template-columns: 1fr;
  }
}
</style>
